<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { DashboardStation } from '@/types/station'
import { useI18n } from '@/i18n'

const props = defineProps<{
  station: DashboardStation
}>()
const { locale, t } = useI18n()
const isOffline = computed(() => props.station.status === 'offline')

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const dateLocale = locale.value === 'th' ? 'th-TH' : 'en-GB'
  return date.toLocaleDateString(dateLocale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  }) + ' ' + date.toLocaleTimeString(dateLocale, {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatNumber = (num: number | undefined, decimals: number = 2) => {
  if (num === undefined || num === null) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

const statusConfig = computed(() => {
  const status = props.station.status || 'active'
  const configs: Record<string, { bg: string; text: string; dot: string }> = {
    active: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
    warning: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
    alert: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    error: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
    offline: { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' },
  }
  return configs[status] || configs.active
})

const diagnosticConfig = computed(() => {
  const configs = {
    CAPABLE: { label: t('Diagnostics capable'), className: 'text-emerald-600' },
    DISABLED: { label: t('Diagnostics disabled'), className: 'text-gray-500' },
    SHADOW: { label: t('Diagnostics shadow'), className: 'text-blue-600' },
    TOPOLOGY_ERROR: { label: t('Topology error'), className: 'text-red-600' },
    UNAVAILABLE: { label: t('Diagnostics unavailable'), className: 'text-amber-600' },
  }
  return configs[props.station.diagnostic_status] || configs.UNAVAILABLE
})
</script>

<template>
  <RouterLink
    :to="`/station/${station.station_id}`"
    class="table-row-link"
    :class="{ 'table-row-link--offline': isOffline }"
  >
    <!-- Station Name -->
    <td class="px-4 py-3">
      <div class="flex items-center space-x-3">
        <div class="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" :class="isOffline ? 'bg-gray-200' : 'bg-blue-100'">
          <svg class="w-4 h-4" :class="isOffline ? 'text-gray-500' : 'text-blue-600'" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
          </svg>
        </div>
        <div class="min-w-0">
          <p class="font-medium text-gray-900 truncate" :title="station.station_name">
            {{ station.station_name }}
          </p>
          <p class="text-xs text-gray-500 whitespace-nowrap">{{ t((station.meter_count || 0) === 1 ? '{count} meter' : '{count} meters', { count: station.meter_count || 0 }) }}</p>
          <p
            class="text-xs whitespace-nowrap"
            :class="diagnosticConfig.className"
            :title="station.diagnostic_topology_error || diagnosticConfig.label"
          >
            {{ diagnosticConfig.label }}
          </p>
        </div>
      </div>
    </td>

    <!-- Status -->
    <td class="px-4 py-3 whitespace-nowrap">
      <div class="flex flex-col items-start gap-1">
        <span
          class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
          :class="[statusConfig.bg, statusConfig.text]"
        >
          <span class="w-1.5 h-1.5 rounded-full mr-1.5" :class="statusConfig.dot"></span>
          {{ t(station.status?.toUpperCase() || 'ACTIVE') }}
        </span>
        <span
          v-if="isOffline && station.last_known_status !== 'active'"
          class="text-[11px] text-gray-400"
        >
          {{ t('Last known status: {status}', { status: t(station.last_known_status.toUpperCase()) }) }}
        </span>
      </div>
    </td>

    <!-- ตัวเลขทุกคอลัมน์: whitespace-nowrap กันค่ากับหน่วยแยกบรรทัด และ
         tabular-nums ให้หลักตรงกันทุกแถว จะกวาดตาลงคอลัมน์เทียบค่าได้ -->
    <!-- Power -->
    <td class="px-4 py-3 text-right whitespace-nowrap tabular-nums">
      <span v-if="isOffline" class="text-gray-400">—</span>
      <template v-else>
        <span class="font-semibold text-blue-600">{{ formatNumber(station.power_kw) }}</span>
        <span class="text-blue-600 text-sm ml-1">kW</span>
      </template>
    </td>

    <!-- Voltage -->
    <td class="px-4 py-3 text-right whitespace-nowrap tabular-nums">
      <span v-if="isOffline" class="text-gray-400">—</span>
      <template v-else>
        <span class="text-gray-700">{{ formatNumber(station.voltage_ll) }}</span>
        <span class="text-gray-400 text-sm ml-1">V</span>
      </template>
    </td>

    <!-- Current -->
    <td class="px-4 py-3 text-right whitespace-nowrap tabular-nums">
      <span v-if="isOffline" class="text-gray-400">—</span>
      <template v-else>
        <span class="text-gray-700">{{ formatNumber(station.current_avg) }}</span>
        <span class="text-gray-400 text-sm ml-1">A</span>
      </template>
    </td>

    <!-- Energy Today -->
    <td class="px-4 py-3 text-right whitespace-nowrap tabular-nums">
      <span v-if="isOffline" class="text-gray-400">—</span>
      <template v-else>
        <span class="font-semibold text-gray-800">{{ formatNumber(station.energy_kwh) }}</span>
        <span class="text-gray-800 text-sm ml-1">kWh</span>
      </template>
    </td>

    <!-- Last Update -->
    <td class="px-4 py-3 text-right text-sm text-gray-500 whitespace-nowrap tabular-nums">
      {{ formatDateTime(station.last_reading) }}
    </td>

    <!-- Action -->
    <td class="px-4 py-3 text-center">
      <span class="text-gray-400 group-hover:text-blue-500 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </span>
    </td>
  </RouterLink>
</template>

<style scoped>
.table-row-link {
  @apply table-row bg-white hover:bg-blue-50 transition-colors cursor-pointer;
}

.table-row-link:hover td {
  @apply bg-blue-50;
}

.table-row-link.table-row-link--offline td,
.table-row-link.table-row-link--offline:hover td {
  @apply bg-gray-50;
}

.table-row-link td {
  @apply transition-colors;
}
</style>
